// src/modules/manualJournal/components/DayBalanceCard.jsx

import React from 'react';
import {
  Card, Row, Col, Statistic, Button, Popover, Form, InputNumber, Space,
  message, Spin,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { ACCOUNT_CATEGORIES, getCategoryIcon } from '../constants';
import { updateDayBalance } from '../api';

// Sub-component for each balance card
function BalanceCardItem({ category, balData, journalDate, onBalanceUpdated, loading }) {
  const [form] = Form.useForm();
  const [editing, setEditing] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const netChange = balData.closing - balData.opening;

  const handleEditClick = () => {
    // Ensure values are numbers, defaulting to 0 if not present
    const openingVal = typeof balData.opening === 'number' ? balData.opening : 0;
    const closingVal = typeof balData.closing === 'number' ? balData.closing : 0;
    
    form.setFieldsValue({
      openingBalance: openingVal,
      closingBalance: closingVal,
    });
    setEditing(true);
  };

  const handleSaveBalance = async (values) => {
    try {
      setSubmitting(true);
      await updateDayBalance(journalDate, category.value, {
        ...values,
        manualOverride: true,
      });
      message.success('Balance updated successfully');
      setEditing(false);
      form.resetFields();
      onBalanceUpdated?.();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update balance');
    } finally {
      setSubmitting(false);
    }
  };

  const editContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSaveBalance}
    >
      <Form.Item
        label="Opening Balance"
        name="openingBalance"
        rules={[
          { required: true, message: 'Please enter opening balance' },
          { type: 'number', message: 'Must be a number' },
        ]}
      >
        <InputNumber
          prefix="₹"
          style={{ width: '100%' }}
          precision={2}
          min={0}
        />
      </Form.Item>

      <Form.Item
        label="Closing Balance"
        name="closingBalance"
        rules={[
          { required: true, message: 'Please enter closing balance' },
          { type: 'number', message: 'Must be a number' },
        ]}
      >
        <InputNumber
          prefix="₹"
          style={{ width: '100%' }}
          precision={2}
          min={0}
        />
      </Form.Item>

      <Space>
        <Button
          size="small"
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          htmlType="submit"
          loading={submitting}
        >
          Save
        </Button>
      </Space>
    </Form>
  );

  return (
    <Col key={category.value} xs={24} sm={12} lg={8}>
      <Card
        size="small"
        style={{ borderLeft: '4px solid #1890ff' }}
        title={(
          <span>
            {category.icon}
            {' '}
            {category.label}
          </span>
        )}
        extra={(
          <Popover
            content={editContent}
            title="Edit Balances"
            trigger="click"
            open={editing}
            onOpenChange={(visible) => {
              if (visible) handleEditClick();
              else setEditing(false);
            }}
          >
            <Button type="text" size="small" icon={<EditOutlined />} />
          </Popover>
        )}
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Statistic
              title="Opening"
              value={balData.opening}
              prefix="₹"
              precision={2}
              valueStyle={{ fontSize: 12 }}
            />
          </Col>
          <Col span={24}>
            <Statistic
              title="Closing"
              value={balData.closing}
              prefix="₹"
              precision={2}
              valueStyle={{ fontSize: 12, color: balData.closing >= balData.opening ? '#52c41a' : '#f5222d' }}
            />
          </Col>
          <Col span={24}>
            <Statistic
              title="Net Change"
              value={netChange}
              prefix="₹"
              precision={2}
              valueStyle={{
                fontSize: 12,
                color: netChange >= 0 ? '#52c41a' : '#f5222d',
              }}
            />
          </Col>
        </Row>
      </Card>
    </Col>
  );
}

export default function DayBalanceCard({ journalDate, balances, onBalanceUpdated, loading }) {
  const getBalance = (category) => {
    const balance = balances?.find((b) => b.accountCategory === category);
    return balance ? { opening: balance.openingBalance, closing: balance.closingBalance } : { opening: 0, closing: 0 };
  };

  return (
    <Spin spinning={loading}>
      <Card title="Account Balances" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          {ACCOUNT_CATEGORIES.map((cat) => {
            const balData = getBalance(cat.value);
            return (
              <BalanceCardItem
                key={cat.value}
                category={cat}
                balData={balData}
                journalDate={journalDate}
                onBalanceUpdated={onBalanceUpdated}
                loading={loading}
              />
            );
          })}
        </Row>
      </Card>
    </Spin>
  );
}
